/**
 * Created by Abner Sui on 09/27/2019
 * -------------------------------------
 * Updated by Daniel on 04/07/2021 added history for approve task
 * Updated by Simon Zhao on 11/30/2021 enabled this component to support the task context change triggered by progress node's selection.
 * Updated by Simon Zhao on 2/21/2022 added the description of current task.
 * Updated by Simon Zhao on 11/28/2022 support field modification of done tasks.
 */

import { Component, OnInit, Input, OnDestroy, ViewChild } from '@angular/core';
import {
    Task,
    TaskStatus,
    FieldType,
    ProcessInstance,
    ProcessDefinition,
    Field,
    TaskType,
    ProcessInstanceEventDetail,
    ProcessInstanceEvent,
    TriggerBy,
    TaskDefinition,
    FileInfo,
    TemplateControlInfo,
} from '../../../tamalelibs/models/workflow.model';
import { ControlType } from '../../../tamalelibs/models/template-control.model';
import { WorkflowTaskService } from '../../../tamalelibs/services/workflow-task.service';
import { catchError, take, debounceTime, switchMap, map, delay } from 'rxjs/operators';
import { of, Subscription, Subject, Observable, forkJoin, ReplaySubject } from 'rxjs';
import { ToastService } from '../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { SlideSheetService } from '../../../services/slide-sheet.service';
import { SlideSheetActionTypes } from '../../slide-sheet/slide-sheet.model';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { SystemUser, UserWorkflowPreference } from '../../../tamalelibs/models/user.model';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { EntityBrief } from '../../../tamalelibs/models/entity-brief.model';
import { AppState } from '../../../redux';
import { Store } from '@ngrx/store';
import {
    ProcessInstanceAddOrUpdate,
    ProcessInstanceAddOrUpdateSuccess,
} from '../../../tamalelibs/redux/actions/workflow.actions';
import { SelectWindowService } from '../../../widgets/select-window/select-window.service';
import { BriefConfig } from '../../../widgets/widgets.model';
import { commentAction } from '../comments/comment.component';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import * as lodash from 'lodash';
import {
    FileUploadAction,
    FileUploadActionTypes,
} from '../../file-upload/file-upload.model';
import {
    GlobalService,
    GlobalActionType,
} from '../../../tamalelibs/services/global.service';
import { FieldConfig, FieldEvents, FieldActions } from '../field/field.model';
import { EntityService } from '../../../tamalelibs/services/entity.service';
import { Entity } from './../../../tamalelibs/models/entity.model';
import { ALL_VALUE } from './../../../tamalelibs/constants/business.constants';
import { ProgressNodeModel } from '../../../widgets/progress/progress.model';
import { ArrayHelperService } from '../../../tamalelibs/services/array-helper.service';
import { ContactService } from '../../../tamalelibs/services/contact.service';
import { EntityType } from '../../../tamalelibs/models/entity-type.model';
import { Contact } from '../../../tamalelibs/models/contact.model';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';
import { UsersActionTypes } from '../../../redux/actions/users.actions';
import { userSettingSelector } from '../../../redux/reducers/users.reducer';
import { businessConstants } from '../../../constants/business.constants';

export class Button {
    type: string;
    size: string;
    text: string;
    disable: boolean;
    onClick: Function;
}

export const TASK_FORM_WIDTH = 620;
export const PROGRESS_COLLAPSE_WIDTH = 56;
export const PROGRESS_EXPAND_WIDTH = 277;
export const HISTORY_WIDTH = 400;
export const TASK_DETAIL_DEFAULT_WIDTH =
    TASK_FORM_WIDTH + PROGRESS_COLLAPSE_WIDTH;

@Component({
    selector: 'tam-task-detail',
    templateUrl: './task-detail.component.html',
    styleUrls: ['./task-detail.component.scss'],
})
export class TaskDetailComponent implements OnInit, OnDestroy {
    @Input() task: Task;
    @Input() instance: ProcessInstance;
    @Input() processDefinition: ProcessDefinition;

    @ViewChild('commentArea', { static: true }) commentArea;
    valueChangeSubject = new ReplaySubject<TemplateControlInfo>(10);
    currentUser: SystemUser;
    buttons: Array<Button> = [];
    editable: boolean;
    feedbackSubject$ = new Subject<any>();
    showPopup = false;
    comment = '';
    commentButtons: Array<Button>;
    showComment = false;
    commentsConfig: BriefConfig = new BriefConfig();
    backTaskList: Array<Task> = [];
    showListInPopup = false;
    backTask: Task;
    deleteWhenCancel = false;
    isProgressExpanded = false;
    showExtra = false;
    tabsData: Array<any> = [
        {
            title: 'History',
            editable: false,
        },
        {
            title: 'Comments',
            editable: false,
        },
    ];
    selectedIndex = 0;
    fileOperating = false;
    buttonOperating = false;
    popupPlaceholder: string;
    progressData: Array<ProgressNodeModel> = [];
    /**
     * the field configuration map for current task.
     *
     * @type {Map<string, FieldConfig>}
     * @memberof TaskDetailComponent
     */
    fields: Map<string, FieldConfig> = new Map();
    /**
     * the cache for task field configurations of a same process.
     *
     * @type {Map<string, Map<string, FieldConfig>>}
     * @memberof TaskDetailComponent
     */
    processFieldsMap: Map<string, Map<string, FieldConfig>> = new Map();
    briefConfig: BriefConfig = new BriefConfig();
    isLoading = true;
    initialProgressExpansionState: boolean;
    previousTasks: Array<Task>;
    showComments: Array<boolean>;
    get taskDescription() {
        const taskDefinitionnOnInstance =
            this.task.processInstance &&
                this.task.processInstance.processDefinition &&
                this.task.processInstance.processDefinition.taskDefinition &&
                this.task.taskDefinition
                ? this.task.processInstance.processDefinition.taskDefinition.find(
                    (taskDef) => taskDef.id === this.task.taskDefinition.id
                )
                : null;
        // server expand api does not copy description attribute from the process definition to the task definition of task instances
        // so traverse the process instance to find the description value from the task definition on the process definition.
        return this.task.taskDefinition && this.task.taskDefinition.description
            ? this.task.taskDefinition.description
            : !!taskDefinitionnOnInstance
                ? taskDefinitionnOnInstance.description
                : '';
    }

    private _autoFocusSubject$: Subject<any> = new Subject();
    private _button: Button;
    private _currentSavedFiles: Map<string, Array<FileInfo>> = new Map();
    private _currentUserContact: Contact;
    private _destroySubscriptions: Array<Subscription> = [];
    private _fieldInvalid: Map<string, boolean> = new Map();
    /**
     * the initial value of each field of current task.
     */
    private _rawFieldValueMap: {};
    private _showExtraSubject: Subject<boolean> = new Subject();
    private _triggerEntity: Entity;
    private _valueChangedFlagMap: Map<string, boolean> = new Map();
    // a flag indicating whether current task is a done input task.
    private _isCurrentInputTaskDone = false;

    constructor(
        private _taskService: WorkflowTaskService,
        private _toast: ToastService,
        private _slideSheetService: SlideSheetService,
        private _storeQuerier: StoreQuerierService,
        private _workflowService: WorkflowService,
        private _store: Store<AppState>,
        private _selectService: SelectWindowService,
        private _alertWindow: AlertWindowService,
        private _globalService: GlobalService,
        private _entityService: EntityService,
        private _contactService: ContactService
    ) { }

    //#region public functions
    //#region NG hooks
    ngOnInit() {
        this.currentUser = this._storeQuerier.getCurrentUser();
        this.editable = this._checkModificationRight(
            this.task,
            this.currentUser
        );
        // get all files in current instance
        this._currentSavedFiles = this._taskService.getAllFilesByInstance(this.instance);

        this._loadProgressExpansionState();
        const initFunc = () => {
            if (
                this.task.processInstance.triggerBy === TriggerBy.SCHEDULE_JOB &&
                this.task.processInstance.triggerValue
            ) {
                // default the entity dropdown with trigger entity switch on and the trigger-by is scheduled-job to the trigger entity of the instance.
                this._destroySubscriptions.push(
                    this._entityService
                        .getEntityDetailWithoutAdhoc(
                            this.task.processInstance.triggerValue
                        )
                        .pipe(take(1))
                        .subscribe(
                            (response) => {
                                this._triggerEntity = Entity.parse(response);
                                this._prepareForTask(this.task, true);
                            },
                            (err) => this._prepareForTask(this.task, true)
                        ));
            } else {
                this._prepareForTask(this.task, true);
            }
        };

        this._destroySubscriptions.push(
            this._contactService
                .getContactDetail(this.currentUser.id)
                .pipe(take(1))
                .subscribe((response) => {
                    this._currentUserContact = Contact.parse(response);
                    initFunc();
                }, err => {
                    initFunc();
                }));
    }

    ngOnDestroy() {
        this._valueChangedFlagMap.clear();
        this._destroySubscriptions.forEach((item) => item.unsubscribe());
        this._globalService.actionSubject$.next({
            type: GlobalActionType.LOCK_DISABLE_GLOBAL_BUFFER,
            payload: false,
        });
        this._globalService.actionSubject$.next({
            type: GlobalActionType.DISABLE_GLOBAL_BUFFER,
            payload: false,
        });
    }
    //#endregion

    fieldInvalid(): boolean {
        if (this.editable || this._isCurrentInputTaskDone) {
            this.briefConfig.actionSubject$.next({
                type: FieldActions.SHOW_VALIDATE_REQUIRE,
            });

            // only check the fields which belong to current task
            const currentInvalidFields = new Map<string, boolean>();
            if (this._fieldInvalid && this._fieldInvalid.size > 0) {
                this._fieldInvalid.forEach((item, key) => {
                    if (Array.from(this.fields.keys()).includes(key)) {
                        currentInvalidFields.set(key, item);
                    }
                });
            }

            return Array.from(currentInvalidFields.values()).some(
                (invalid) => invalid
            );
        } else {
            return true;
        }
    }

    getApproveStatus(task: Task): string {
        let result = '';
        if (task.taskDefinition.type === TaskType.APPROVAL) {
            const oneApprove = task.approvalTask.find(
                (item) => item.user.id === this.currentUser.id
            );
            if (oneApprove) {
                result =
                    oneApprove.status === TaskStatus.DONE
                        ? 'Approved'
                        : oneApprove.status === TaskStatus.REJECTED
                            ? 'Rejected'
                            : '';
            }
            const overallstatus =
                task.status === TaskStatus.DONE
                    ? 'Approved'
                    : task.status === TaskStatus.REJECTED
                        ? 'Rejected'
                        : '';
            if (overallstatus) {
                if (result) {
                    result += ' / ' + overallstatus;
                } else {
                    result = overallstatus;
                }
            }
        }
        return result;
    }

    getDisplayValueForEntities(entities: Array<EntityBrief>): string {
        return this._workflowService.getDisplayValueForEntities(entities);
    }

    getFields(oneTask: Task): Map<string, Field> {
        if (
            oneTask.status === TaskStatus.TODO ||
            oneTask.status === TaskStatus.IN_PROGRESS
        ) {
            return oneTask.taskDefinition.fields;
        } else {
            return oneTask.fields;
        }
    }

    onChangeBackTask(event): void {
        this.backTask = event;
    }

    onClickApprove(event, button: Button): void {
        this.onClickPopupCancel(event);
        this.onClickPopupButton(event, button, this._approve);
    }

    onClickCancel(event): void {
        if (this._isAnyTaskValueChanged()) {
            const subscription = this._alertWindow
                .warn(
                    'You have unsaved changes',
                    ['Are you sure you want to discard the changes?'],
                    'Discard',
                    'Go Back',
                    'slideSheet',
                    true
                )
                .subscribe((result: boolean) => {
                    if (result) {
                        this._clearAttachments('cancel')
                            .pipe(take(1))
                            .subscribe((item) => {
                                this._slideSheetService.slideSheetActionSubject$.next(
                                    {
                                        type: SlideSheetActionTypes.CLOSE,
                                        payload: this.deleteWhenCancel
                                            ? null
                                            : {
                                                isTriggerNewFlagNeedToReset:
                                                    true,
                                            },
                                    }
                                );
                            });
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._slideSheetService.slideSheetActionSubject$.next({
                type: SlideSheetActionTypes.CLOSE,
                payload: this.deleteWhenCancel
                    ? null
                    : { isTriggerNewFlagNeedToReset: true },
            });
        }
    }

    onClickPopupCancel(event): void {
        this.showPopup = false;
        this.comment = '';
        if (this._button) {
            this._button.disable = false;
        }
        this.showListInPopup = false;
        this.backTask = null;
    }

    onClickComplete(event, button: Button): void {
        if (!this.fieldInvalid()) {
            this.buttonOperating = true;
            this._clearAttachments('save')
                .pipe(take(1))
                .subscribe((ev) => {
                    const fieldsPara: any = this._parseFieldsParameter();
                    const deletedFiles = this._getDeletedFiles();
                    this._taskService
                        .saveTask(this.task.id, fieldsPara, deletedFiles, true)
                        .pipe(
                            catchError((err) => {
                                if (err.message) {
                                    this._errorMessageHandling(err.message);
                                }
                                return of(null);
                            }),
                            take(1)
                        )
                        .subscribe((res) => {
                            if (res) {
                                this._toast.notify({
                                    style: NotificationStyles.Success,
                                    message: `Task '${this.task.name}' successfully submitted.`,
                                });
                            }
                            this._slideSheetService.slideSheetActionSubject$.next(
                                {
                                    type: SlideSheetActionTypes.CLOSE,
                                    payload: {
                                        isTriggerNewFlagNeedToReset: true,
                                    },
                                }
                            );
                            this._store.dispatch(
                                new ProcessInstanceAddOrUpdate({
                                    id: this.instance.id,
                                    processDefinitionId:
                                        this.processDefinition.id,
                                    template: this.processDefinition.template,
                                })
                            );
                            this.buttonOperating = false;
                        });
                });
        }
    }

    onClickPopupButton(
        event,
        button: Button,
        newClickFunction: Function
    ): void {
        this.showPopup = true;
        const tempButton: Button = Object.assign({}, button);
        this.popupPlaceholder =
            'You could ' +
            tempButton.text.toLowerCase() +
            ' directly without comments';
        this.commentButtons = [
            {
                type: 'tertiary',
                size: tempButton.size,
                text: 'Cancel',
                disable: false,
                onClick: this.onClickPopupCancel.bind(this),
            },
        ];
        tempButton.onClick = newClickFunction.bind(this);
        this.commentButtons.push(tempButton);
        this._button = button;
        this.comment = '';
        this._autoFocusSubject$.next('');
        this._button.disable = true;
    }

    onClickReject(event, button: Button): void {
        this.onClickPopupCancel(event);
        this.onClickPopupButton(event, button, this._reject);
    }

    onClickSave(event, button: Button): void {
        this.buttonOperating = true;
        this._clearAttachments('save')
            .pipe(take(1))
            .subscribe((ev) => {
                const fieldsPara: any = this._parseFieldsParameter();
                const deletedFiles = this._getDeletedFiles();
                this._taskService
                    .saveTask(this.task.id, fieldsPara, deletedFiles)
                    .pipe(
                        catchError((err) => {
                            if (err.message) {
                                this._errorMessageHandling(err.message);
                            }
                            return of(null);
                        }),
                        take(1)
                    )
                    .subscribe((res) => {
                        if (res) {
                            this._toast.notify({
                                style: NotificationStyles.Success,
                                message: `Task '${this.task.name}' successfully saved.`,
                            });
                        }
                        this._slideSheetService.slideSheetActionSubject$.next({
                            type: SlideSheetActionTypes.CLOSE,
                            payload: {
                                isTriggerNewFlagNeedToReset: true,
                            },
                        });
                        this._store.dispatch(
                            new ProcessInstanceAddOrUpdate({
                                id: this.instance.id,
                                processDefinitionId: this.processDefinition.id,
                                template: this.processDefinition.template,
                            })
                        );
                        this.buttonOperating = false;
                    });
            });
    }

    onClickDoneTaskSave(event, button: Button): void {
        if (!this.fieldInvalid()) {
            this.buttonOperating = true;
            const subscription = this._alertWindow
                .warn(
                    StringLiteralsPipe.translate('general.you_r_making_changes'),
                    [StringLiteralsPipe.translate('general.do_you_want_make_change'),
                    StringLiteralsPipe.translate('general.task_has_been_completed')],
                    StringLiteralsPipe.translate('general.yes_want_make_change'),
                    StringLiteralsPipe.translate('general.cancel'),
                    'slideSheet',
                    true
                )
                .subscribe((result: boolean) => {
                    const endSubFunc = () => {
                        if (subscription) {
                            subscription.unsubscribe();
                        }
                        this.buttonOperating = false;
                    };
                    if (result) {
                        const currentValue = this._parseFieldsParameter();
                        const sortOutChangedFields = (src: {}, tar: {}) => {
                            const changedFields = [];
                            Object.keys(src).forEach(
                                (fieldId: string) => {
                                    if (src[fieldId] !== tar[fieldId]) {
                                        changedFields.push(fieldId);
                                    }
                                }
                            );
                            return changedFields;
                        };
                        const changedFieldIds = sortOutChangedFields(this._rawFieldValueMap, currentValue);
                        const deletedFileIds = this._getDeletedFiles();
                        if (changedFieldIds.length > 0) {
                            this._taskService.updateTask(this.task.id, currentValue, changedFieldIds, deletedFileIds).pipe(
                                catchError((err) => {
                                    if (err.message) {
                                        this._errorMessageHandling(err.message);
                                    }
                                    return of(null);
                                }),
                                take(1)
                            )
                                .subscribe((res) => {
                                    if (res) {
                                        this._toast.notify({
                                            style: NotificationStyles.Success,
                                            message: StringLiteralsPipe.translate('general.task_has_been_updated', this.task.name),
                                        });
                                    }
                                    this._slideSheetService.slideSheetActionSubject$.next({
                                        type: SlideSheetActionTypes.CLOSE,
                                        payload: {
                                            isTriggerNewFlagNeedToReset: true,
                                        },
                                    });
                                    this._store.dispatch(
                                        new ProcessInstanceAddOrUpdate({
                                            id: this.instance.id,
                                            processDefinitionId: this.processDefinition.id,
                                            template: this.processDefinition.template,
                                        })
                                    );
                                    endSubFunc();
                                });
                        }
                    } else {
                        endSubFunc();
                    }
                });
        }
    }

    onClickSendBack(event, button: Button): void {
        this.onClickPopupCancel(event);
        this.showListInPopup = true;
        // Per the requirment, default to select the most recent task on the send-back target list.
        this.backTask = ArrayHelperService.max<Task>(this.backTaskList, 'lastModifiedDate');
        this.onClickPopupButton(event, button, this._sendBack);
    }

    onClickShowComment(event, index) {
        if (index === null) {
            this.showComment = !this.showComment;
        } else {
            this.showComments[index] = !this.showComments[index];
        }
    }

    /**
     * handle the expand/collapse event of progress widget
     *
     * @param {boolean} isExpanded a flag indicating the progress is being expanded.
     * @param {boolean} isStateSaving a flag indicating the new state should be saved.
     * @memberof TaskDetailComponent
     */
    onProgressWidthChanged(isExpanded: boolean, isStateSaving = true) {
        this.isProgressExpanded = isExpanded;
        if (isStateSaving) {
            this._saveProgressBarExpansionState(isExpanded);
        }
        // Delay this action emission as there might be another action, task selection action, emitted at the same time and this emission may mess it up. So delay 200ms to avoid it.
        const thatRef = this;
        of(null).pipe(delay(200)).subscribe(r => {
            thatRef._slideSheetService.slideSheetActionSubject$.next({
                type: SlideSheetActionTypes.CHANGE_WIDTH,
                payload: thatRef._getWidth(thatRef.isProgressExpanded, thatRef.showExtra),
            });
        });
    }

    /**
     * handle the progress node click event
     *
     * @param {ProgressNodeModel} node the corresponding progress node.
     * @memberof TaskDetailComponent
     */
    onProgressNodeClicked(node: ProgressNodeModel) {
        if (
            !!node &&
            !!node.nodeContext &&
            node.nodeContext.id !== this.task.id
        ) {
            // a different task instance node is clicked.
            this.task = node.nodeContext;
            this._prepareForTask(this.task);
        }
    }

    selectChange(event): void {
        this.selectedIndex = event;
    }

    showExtraInformation(event): void {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CHANGE_WIDTH,
            payload: this._getWidth(this.isProgressExpanded, !this.showExtra),
        });
        this._showExtraSubject.next(!this.showExtra);
    }
    //#endregion

    //#region private functions
    private _addComment(action): void {
        if (action.type === commentAction.SAVE && action.payload.comment) {
            const parentId: string = action.payload.parentId;
            const comm: string = action.payload.comment;
            let tempTask: Task = this.previousTasks.find(
                (item) =>
                    item.comments &&
                    item.comments.find((com) => com.id === parentId)
            );
            if (!tempTask) {
                if (
                    this.task.comments &&
                    this.task.comments.find((com) => com.id === parentId)
                ) {
                    tempTask = this.task;
                }
            }
            if (tempTask) {
                const taskId: string = tempTask.id;
                this._taskService
                    .addComment(taskId, {
                        comment: comm,
                        parentCommentId: parentId,
                    })
                    .pipe(
                        catchError((err) => {
                            return of(null);
                        }),
                        switchMap(() => {
                            return this._workflowService.getProcessInstanceById(
                                this.instance.id
                            );
                        }),
                        catchError((err) => {
                            return of(null);
                        }),
                        take(1)
                    )
                    .subscribe((res) => {
                        if (res) {
                            this.instance = ProcessInstance.parse(
                                res,
                                this.processDefinition.id,
                                this.processDefinition.template
                            );
                            this._store.dispatch(
                                new ProcessInstanceAddOrUpdateSuccess(
                                    this.instance
                                )
                            );
                            const task: Task = this.instance.task.find(
                                (item) => item.id === this.task.id
                            );
                            this._slideSheetService.showSlideSheetForTask(
                                null,
                                task,
                                this.instance,
                                this.processDefinition,
                                this._taskService.getTaskEditable(
                                    task,
                                    this.currentUser
                                )
                            );
                        } else {
                            this._store.dispatch(
                                new ProcessInstanceAddOrUpdate({
                                    id: this.instance.id,
                                    processDefinitionId:
                                        this.processDefinition.id,
                                    template: this.processDefinition.template,
                                })
                            );
                        }
                    });
            }
        }
    }

    private _approve(event): void {
        this.buttonOperating = true;
        this._taskService
            .approveTaskById(
                this.task.id,
                true,
                null,
                this.comment ? { comment: this.comment } : null
            )
            .pipe(
                catchError((err) => {
                    if (err.message) {
                        if (err.message.includes('ERR_PROJECT_DELETED')) {
                            this._toast.notify({
                                style: NotificationStyles.Error,
                                message: businessConstants.workflow.approve.projectWasDeletedMsg
                            });
                        } else {
                            this._toast.notify({
                                style: NotificationStyles.Error,
                                message: businessConstants.workflow.approve.taskWasFinishedMsg
                            });
                        }
                    }
                    return of(null);
                }),
                take(1)
            )
            .subscribe((res) => {
                if (res) {
                    this._toast.notify({
                        style: NotificationStyles.Success,
                        message: `Task '${this.task.name}' successfully approved.`,
                    });
                }
                this._slideSheetService.slideSheetActionSubject$.next({
                    type: SlideSheetActionTypes.CLOSE,
                    payload: { isTriggerNewFlagNeedToReset: true },
                });
                this._store.dispatch(
                    new ProcessInstanceAddOrUpdate({
                        id: this.instance.id,
                        processDefinitionId: this.processDefinition.id,
                        template: this.processDefinition.template,
                    })
                );
                this.buttonOperating = false;
            });
    }

    private _checkModificationRight(t: Task, usr: SystemUser) {
        if (Task.isManualTask(t)) {
            return this._taskService.getTaskEditable(t, usr);
        } else {
            return false;
        }
    }

    private _clearAttachments(workflow): Observable<any> {
        const observables: Array<Observable<any>> = [];
        const actions: Map<string, FileUploadAction> = new Map();
        const fileFieldConfig: Map<string, FieldConfig> = new Map();
        this.fields.forEach((value, key, m) => {
            if (value.field.fieldDefinition.type === FieldType.FILE && value.editable) {
                const action = new FileUploadAction();
                action.type = FileUploadActionTypes.DeleteAttachments;
                action.payload = {
                    workflow: workflow,
                    subject: new Subject(),
                };
                observables.push(
                    action.payload.subject.pipe(debounceTime(0), take(1))
                );
                actions.set(key, action);
                fileFieldConfig.set(key, value);
            }
        });
        if (observables.length) {
            const tempSubject = new Subject();
            forkJoin(observables).subscribe((event) => {
                tempSubject.next(event);
            });
            fileFieldConfig.forEach((value, key, m) => {
                value.config.actionSubject$.next(actions.get(key));
            });
            return tempSubject.pipe();
        } else {
            const observable = new Observable((subscriber) => {
                subscriber.next();
                subscriber.complete();
            });
            return observable.pipe(
                switchMap((item) => {
                    return of('');
                })
            );
        }
    }

    private _errorMessageHandling(errMassage: string): void {
        if (errMassage.indexOf('The Task had been completed') > -1) {
            this._toast.notify({
                style: NotificationStyles.Error,
                message: 'The Task had been completed.',
            });
        } else if (errMassage.indexOf('The project had been completed') > -1) {
            this._toast.notify({
                style: NotificationStyles.Error,
                message: 'The project had been completed.',
            });
        } else if (errMassage.indexOf('The task status incorrect!') > -1) {
            this._toast.notify({
                style: NotificationStyles.Error,
                message: 'Someone else has already finished this task',
            });
        } else if (errMassage.indexOf('The task owner incorrect!') > -1) {
            this._toast.notify({
                style: NotificationStyles.Error,
                message: 'Someone else is already working on this task',
            });
        } else if (errMassage.indexOf('The project had been rejected') > -1) {
            this._toast.notify({
                style: NotificationStyles.Error,
                message: 'The project had been rejected. can NOT operate it!',
            });
        } else if (
            errMassage.search(
                'The project with ID /^[a-zA-Z0-9]8$/ had been deleted'
            )
        ) {
            const projectName = this.instance.projectName;
            const msg = 'Project ' + projectName + ' was not found.';
            this._toast.notify({
                style: NotificationStyles.Error,
                message: msg,
            });
        }
    }

    private _loadProgressExpansionState() {
        const userSetting = this._storeQuerier.queryBySelector(userSettingSelector);
        if (userSetting) {
            const wfPreference = UserWorkflowPreference.parseUserSetting(userSetting);
            // default progress pane to be expanded.
            this.initialProgressExpansionState = wfPreference.isProgressExpanded === false ? false : true;
            this.onProgressWidthChanged(this.initialProgressExpansionState, false);
        }
    }

    // get deleted files by loading files and current fields
    private _getDeletedFiles() {
        const result = {};
        this._currentSavedFiles.forEach((value, key) => {
            const field = this.fields.get(key);
            if (field) {
                const deletedFileIds = [];
                // use id of fileInfo to judege whether the file is deleted or renamed
                const currentIds = field.field.value.map(item => (item.id || item.originalId));
                value.forEach(file => {
                    if (!ArrayHelperService.arrayContains(currentIds, file.id)) {
                        deletedFileIds.push(file.id);
                    }
                });
                if (deletedFileIds.length > 0) {
                    result[key] = deletedFileIds;
                }
            }
        });
        return result;
    }

    private _getWidth(isProgressExpanded: boolean, isShowExtra: boolean) {
        const baseWidth = isShowExtra
            ? TASK_FORM_WIDTH + HISTORY_WIDTH
            : TASK_FORM_WIDTH;
        const progressWidth = isProgressExpanded
            ? PROGRESS_EXPAND_WIDTH
            : PROGRESS_COLLAPSE_WIDTH;
        return baseWidth + progressWidth;
    }

    private _initButtons(t: Task): void {
        this._isCurrentInputTaskDone = false;
        if (t.taskDefinition.type === TaskType.APPROVAL) {
            if (
                t.status === TaskStatus.IN_PROGRESS ||
                t.status === TaskStatus.TODO
            ) {
                this.buttons = [
                    {
                        type: 'tertiary',
                        size: 'normal',
                        text: StringLiteralsPipe.translate('general.cancel'),
                        disable: false,
                        onClick: this.onClickCancel.bind(this),
                    },
                    {
                        type: 'secondary',
                        size: 'normal',
                        text: StringLiteralsPipe.translate('general.request_changes'),
                        // either current user is NOT the assignee of the given task or there's no input task before current task.
                        disable:
                            !this.editable || this.backTaskList.length === 0,
                        onClick: this.onClickSendBack.bind(this),
                    },
                    {
                        type: 'destructive',
                        size: 'normal',
                        text: StringLiteralsPipe.translate('general.reject'),
                        disable: !this.editable,
                        onClick: this.onClickReject.bind(this),
                    },
                    {
                        type: 'primary-success',
                        size: 'normal',
                        text: StringLiteralsPipe.translate('general.approve'),
                        disable: !this.editable,
                        onClick: this.onClickApprove.bind(this),
                    },
                ];
            } else {
                this.buttons = [];
            }
        } else if (t.taskDefinition.type === TaskType.INPUT) {
            if (this.editable) {
                this._globalService.actionSubject$.next({
                    type: GlobalActionType.LOCK_DISABLE_GLOBAL_BUFFER,
                    payload: false,
                });
                this._globalService.actionSubject$.next({
                    type: GlobalActionType.DISABLE_GLOBAL_BUFFER,
                    payload: true,
                });
                this._globalService.actionSubject$.next({
                    type: GlobalActionType.LOCK_DISABLE_GLOBAL_BUFFER,
                    payload: true,
                });
            }
            if (
                t.status === TaskStatus.IN_PROGRESS ||
                t.status === TaskStatus.TODO
            ) {
                this.buttons = [
                    {
                        type: 'tertiary',
                        size: 'normal',
                        text: StringLiteralsPipe.translate('general.cancel'),
                        disable: false,
                        onClick: this.onClickCancel.bind(this),
                    },
                    {
                        type: 'secondary',
                        size: 'normal',
                        text: StringLiteralsPipe.translate('general.save'),
                        disable: !this.editable,
                        onClick: this.onClickSave.bind(this),
                    },
                    {
                        type: 'primary',
                        size: 'normal',
                        text: StringLiteralsPipe.translate('general.complete'),
                        disable: !this.editable,
                        onClick: this.onClickComplete.bind(this),
                    },
                ];
            } else if (t.status === TaskStatus.DONE && this._isUsrAdminOrTaskOwner(t, this.currentUser)) {
                this.buttons = [{
                    type: 'primary',
                    size: 'normal',
                    text: StringLiteralsPipe.translate('general.save'),
                    disable: true,
                    onClick: this.onClickDoneTaskSave.bind(this),
                }];
                this._isCurrentInputTaskDone = true;
            } else {
                this.buttons = [];
            }
            if (
                this.processDefinition.configuration.startTask ===
                t.taskDefinition.id &&
                this.instance.task.filter((item) => item.taskDefinition)
                    .length === 1 &&
                !this.instance.processInstanceEvent.length &&
                t.status === TaskStatus.TODO
            ) {
                if (this.instance.originator.id === this.currentUser.id) {
                    this.deleteWhenCancel = true;
                }
            }
        } else {
            this.buttons = [];
        }
    }

    /**
     * Determines whether the given user is the owner of the given task or administrator.
     * @param task the given task
     * @param user current user
     * @returns a flag indicating whether the given user is admin or the owner of the given task.
     */
    private _isUsrAdminOrTaskOwner(task: Task, user: SystemUser) {
        if (task && user) {
            const isAdminUser = user && user.credential && user.credential.admin;
            const isTaskOwner = task.owner && task.owner.id === user.id;
            const isTaskAssignee = task.assignee && task.assignee.findIndex(ass => ass.id === user.id) > -1;
            return isAdminUser || isTaskOwner || isTaskAssignee;
        }
        return false;
    }

    /**
     * determines whether there's any change made since this task detail page is open.
     * @returns a flag indicating whether there's any task with any value change.
     */
    private _isAnyTaskValueChanged() {
        // this logic is based on current implementation, there's nowhere to set value-change flag as false.
        return this._valueChangedFlagMap.size > 0;
    }

    private _initAdhocValueFromPreviousOrTemplate(t: Task): void {
        const length = this.instance.processInstanceEvent.length;
        if (length) {
            const processInstanceEvent: Array<ProcessInstanceEvent> =
                this.instance.processInstanceEvent.sort(
                    (a, b) => b.operateTime.getTime() - a.operateTime.getTime()
                );
            let tempTask: ProcessInstanceEventDetail;
            for (let i = 0; i < length; i++) {
                tempTask = processInstanceEvent[i].detail.find(
                    (item) =>
                        item.taskDefinition &&
                        item.taskDefinition.id === t.taskDefinition.id
                );
                if (tempTask) {
                    break;
                }
            }
            if (
                tempTask &&
                tempTask.fields &&
                tempTask.fields.size &&
                t.taskDefinition.fields
            ) {
                const tempFields: Map<string, Field> = t.taskDefinition.fields;
                Array.from(tempFields.keys()).forEach((key) => {
                    const currentValue: Field = tempFields.get(key);
                    const possibbleValue: Field = tempTask.fields.get(key);
                    if (
                        currentValue &&
                        !currentValue.value &&
                        possibbleValue &&
                        possibbleValue.value
                    ) {
                        currentValue.value = possibbleValue.value;
                    }
                });
            }
        } else {
            t.taskDefinition.template.contains.forEach((row) => {
                row.forEach((control) => {
                    this._initValueForField(control, this._triggerEntity, this._currentUserContact, t.processInstance.triggerValue);
                });
            });
        }
    }

    /**
     * Initializes a field control with default value configs.
     * @param control the field control with default value configs.
     * @param triggerEntity current project's trigger entity's type.
     * @param currentUsr current user's contact.
     * @param triggerValue the trigger entity.
     */
    private _initValueForField(control: any, triggerEntity: Entity, currentUsr: Contact, triggerValue: string) {
        if (
            control &&
            control.field &&
            (control.field.value === null ||
                control.field.value === undefined)
        ) {// no saved value needs to be loaded back, so default with configured ones.
            let entityDefaultValues = [];
            if (control.staticDefaultValue) {
                // static default value is configured.
                switch (control.type) {
                    case ControlType.SINGLE_ENTITY_DROP_DOWN:
                    case ControlType.MULTI_ENTITY_DROP_DOWN:
                    case ControlType.SINGLE_USER_DROP_DOWN:
                        if (Array.isArray(control.staticDefaultValue)) {
                            entityDefaultValues.push(...control.staticDefaultValue);
                        } else {
                            entityDefaultValues.push(control.staticDefaultValue);
                        }
                        break;
                    case ControlType.DATE:
                        control.field.value = new Date();
                        break;
                    default:
                        control.field.value = control.staticDefaultValue;
                        break;
                }
            }

            const isAllOrSpecifiedTypeFunc = (typeId: string, checkedTypes: Array<string>) => {
                return !!checkedTypes && (Array.from(checkedTypes).indexOf(
                    typeId
                ) >= 0 || Array.from(checkedTypes).indexOf(ALL_VALUE) >= 0);
            };

            if (control.isTriggerEntityAsDefaultEnabled && triggerEntity && isAllOrSpecifiedTypeFunc(triggerEntity.type.id, control.source)) {
                // trigger entity as default is enabled.
                const defaultEntity = new EntityBrief(
                    triggerValue,
                    triggerEntity.shortName
                );
                defaultEntity.type = triggerEntity.type;
                defaultEntity.shortName = defaultEntity.name;
                switch (control.type) {
                    case ControlType.MULTI_ENTITY_DROP_DOWN:
                        entityDefaultValues.push(defaultEntity);
                        break;
                    case ControlType.SINGLE_ENTITY_DROP_DOWN:
                    case ControlType.SINGLE_USER_DROP_DOWN:
                        // single entity dropdown take trigger entity high priority than static value.
                        entityDefaultValues = [defaultEntity];
                        break;
                    default:
                        break;
                }
            }

            if (control.isCurrentUserAsDefaultEnabled && currentUsr && isAllOrSpecifiedTypeFunc(EntityType.CONTACT.id, control.source)) {
                // current user as default is enabled.
                const currentContact = new EntityBrief(
                    currentUsr.id,
                    currentUsr.name
                );
                currentContact.type = currentUsr.type;
                currentContact.shortName = currentContact.name;
                switch (control.type) {
                    case ControlType.MULTI_ENTITY_DROP_DOWN:
                        entityDefaultValues.push(currentContact);
                        break;
                    case ControlType.SINGLE_ENTITY_DROP_DOWN:
                    case ControlType.SINGLE_USER_DROP_DOWN:
                        // single entity dropdown take trigger entity high priority than static value.
                        entityDefaultValues = [currentContact];
                        break;
                    default:
                        break;
                }
            }

            switch (control.type) {
                case ControlType.SINGLE_ENTITY_DROP_DOWN:
                case ControlType.MULTI_ENTITY_DROP_DOWN:
                case ControlType.SINGLE_USER_DROP_DOWN:
                    if (entityDefaultValues.length > 0) {
                        control.field.value = entityDefaultValues;
                    }
                    break;
                default:
                    break;
            }
        }
    }

    private _isNeedLoadAdhocValueFromPreiousOrTemplate(task: Task): boolean {
        let isNeedLoad = false;

        if (!task.submittedDate ||
            (task.processInstance && task.processInstance.triggerBy === TriggerBy.NOTE_DEPOSIT)) {
            isNeedLoad = true;
        }

        return isNeedLoad;
    }

    private _onEvents(event): void {
        if (event.type === FieldEvents.VALIDATE_CHANGE) {
            this._fieldInvalid.set(event.payload.id, event.payload.invalid);
        } else if (event.type === FieldEvents.VALUE_CHANGE) {
            this._setTaskValueChangeFlag(this.task.id, true);
            this.feedbackSubject$.next(true);
        } else if (event.type === FieldEvents.FILE_OPERATING) {
            this.fileOperating = event.payload;
        }
    }

    /**
     * this function is called when the task context is changed.
     * And there're two scenarios involved:
     * 1. the initialization triggered by clicking a instance/task card in Kan-ban view.
     * 2. the task context is changed by switching between task nodes via clicking on the progress widget.
     *
     * @private
     * @param {Task} t the new task instance.
     * @param {boolean} [isInit=false] a flag indicating whether it's scenario #1.
     * @memberof TaskDetailComponent
     */
    private _prepareForTask(t: Task, isInit = false) {
        this.isLoading = true;
        this.editable = this._checkModificationRight(t, this.currentUser);
        const cachedTaskFields = this.processFieldsMap.get(t.id);
        this.fields.clear();
        if (!cachedTaskFields) {
            // No cached fields found for current task. collect them from task.taskDefiniton.fields.
            t.taskDefinition.fields.forEach((value, key) => {
                // collect the field config info for each field, as some of them the corresponding config on the template doesn't have.
                const oneConfig = new FieldConfig();
                const clonedField = lodash.clone(value);
                oneConfig.field = clonedField;
                oneConfig.editable = this.editable;
                oneConfig.config = this.briefConfig;
                oneConfig.type = FieldConfig.convertFieldTypeToControlType(
                    value.fieldDefinition.type
                );
                this.fields.set(key, oneConfig);
            });
        } else {
            // cached fields found, populate them into variable fields.
            cachedTaskFields.forEach((val: FieldConfig, key: string) => {
                this.fields.set(key, val);
            });
        }

        this.processFieldsMap.set(t.id, new Map(this.fields));

        if (t.taskDefinition.template) {
            // Will change if condition to this.task.taskDefinition.type === TaskType.INPUT, because all input tasks have template, but related feature is under development, need wait
            t.taskDefinition.template.contains.forEach((row) => {
                row.forEach((control) => {
                    const oneConfig = this.fields.get(
                        control.fieldDefinitionId
                    );
                    if (oneConfig) {
                        if (!this.editable && this._isUsrAdminOrTaskOwner(t, this.currentUser)) {
                            // for admin or the owner of current task, if current ui is non-editable,
                            // we should re-evaluate it based on the field config
                            control.editable = oneConfig.editable = !!control.isChangeAfterSubmissionEnabled;
                        } else {
                            control.editable = this.editable;
                        }
                        control.field = oneConfig.field;
                        control.config = this.briefConfig;
                        oneConfig.type = control.type;
                        if (control.type === ControlType.NUMBER) {
                            control.field.fieldDefinition.configuration =
                                control;
                        }
                    }
                });
            });
        }
        // The below function will get all tasks
        this.previousTasks = this._workflowService.getPreviouTasksByTaskStatus(
            this.instance,
            new BriefConfig(),
            this.fields,
            t
        );
        this.showComments = new Array(this.previousTasks.length);
        if (
            t &&
            t.taskDefinition &&
            t.taskDefinition.type === TaskType.APPROVAL
        ) {
            // only fetch send back target list for approval tasks.
            this._taskService.getSendBackTarget(t.id).subscribe(
                (targetList) => {
                    this.backTaskList = [
                        ...targetList.map((targetItem) => {
                            const task = new Task();
                            task.id = targetItem.taskID;
                            task.name = targetItem.taskName;
                            task.lastModifiedDate = targetItem.completeTime;
                            return task;
                        }),
                    ];
                    ArrayHelperService.sort<Task>(this.backTaskList, 'name');
                    // the status of send-back button depends on the result.
                    this._initButtons(t);
                },
                (err) => {
                    this._initButtons(t);
                }
            );
        } else {
            this._initButtons(t);
        }

        if (isInit) {
            this._destroySubscriptions.push(
                this._autoFocusSubject$
                    .pipe(debounceTime(0))
                    .subscribe((action) => {
                        this.commentArea.nativeElement.focus();
                    }),

                this.commentsConfig.feedbackSubject$.subscribe((event) =>
                    this._addComment(event)
                ),

                this._showExtraSubject
                    .pipe(debounceTime(0))
                    .subscribe((data) => (this.showExtra = data)),

                this.briefConfig.feedbackSubject$.subscribe((event) =>
                    this._onEvents(event)
                )
            );
        }
        // init staticDefault value by id(only singleEntityDropDown need it)
        if (t.taskDefinition.template) {
            if (this.editable) {
                const entityId: Set<string> = new Set();
                t.taskDefinition.template.contains.forEach((row) => {
                    row.forEach((control) => {
                        if (control.type === ControlType.SINGLE_ENTITY_DROP_DOWN ||
                            control.type === ControlType.SINGLE_USER_DROP_DOWN) {
                            if (control.staticDefaultValue &&
                                typeof control.staticDefaultValue === 'string') {
                                entityId.add(control.staticDefaultValue);
                            }
                        }
                    });
                });
                if (entityId.size > 0) {
                    this._entityService
                        .getEntityListByIdsQuick(Array.from(entityId))
                        .pipe(
                            take(1),
                            map((response) =>
                                this._entityService.mapEntityBriefList(response)
                            )
                        )
                        .subscribe((entityRes: Array<EntityBrief>) => {
                            t.taskDefinition.template.contains.forEach(
                                (row) => {
                                    row.forEach((control) => {
                                        if (control.type === ControlType.SINGLE_ENTITY_DROP_DOWN ||
                                            control.type === ControlType.SINGLE_USER_DROP_DOWN) {
                                            if (control.staticDefaultValue) {
                                                control.staticDefaultValue =
                                                    entityRes.find(
                                                        (item) =>
                                                            item.id ===
                                                            control.staticDefaultValue
                                                    );
                                            }
                                        }
                                    });
                                }
                            );

                            if (this._isNeedLoadAdhocValueFromPreiousOrTemplate(t)) {
                                this._initAdhocValueFromPreviousOrTemplate(t);
                            }
                            this.isLoading = false;
                        });
                } else {
                    if (this._isNeedLoadAdhocValueFromPreiousOrTemplate(t)) {
                        this._initAdhocValueFromPreviousOrTemplate(t);
                    }
                    this.isLoading = false;
                }
            } else {
                this.isLoading = false;
            }
        } else {
            this.isLoading = false;
        }

        if (isInit) {
            // the progress info would not be changed during switching between progress nodes, so does progress data.
            this.progressData = this._serveProgressData(
                this.instance,
                this.processDefinition
            );
        } else {
            // update the highlighted node on progress
            this.progressData.forEach(
                (n) =>
                (n.isHighlighted =
                    n.nodeContext && n.nodeContext.id === this.task.id)
            );
        }
        this._rawFieldValueMap = this._parseFieldsParameter();
    }

    private _parseFieldsParameter(): {} {
        const result = {};
        this.fields.forEach((fieldCfg, key) => {
            const field = fieldCfg.field;
            if (
                field.value !== null &&
                field.value !== undefined &&
                field.value !== ''
            ) {
                if (field.fieldDefinition.type === FieldType.ENTITY) {
                    if (field.value.length) {
                        result[field.fieldDefinition.id] = field.value
                            .map((item) => item.id)
                            .join(',');
                    }
                } else if (field.fieldDefinition.type === FieldType.User) {
                    if (field.value instanceof Array) {
                        result[field.fieldDefinition.id] = field.value[0].id;
                    } else {
                        result[field.fieldDefinition.id] = field.value.id;
                    }
                } else if (field.fieldDefinition.type === FieldType.DATE) {
                    result[field.fieldDefinition.id] = field.value.getTime();
                } else if (
                    field.fieldDefinition.type === FieldType.NUMBER ||
                    field.fieldDefinition.type === FieldType.CURRENCY
                ) {
                    result[field.fieldDefinition.id] = +field.value;
                } else if (field.fieldDefinition.type === FieldType.FILE) {
                    if (field.value.length) {
                        const tempVal = field.value.map((item) => {
                            if (item.id) {
                                return (
                                    item.id +
                                    ':' +
                                    item.serverFileName +
                                    ':' +
                                    item.fileName
                                );
                            } else {
                                return (
                                    '________:' +
                                    item.serverFileName +
                                    ':' +
                                    item.fileName
                                );
                            }
                        });
                        result[field.fieldDefinition.id] = tempVal.join(',');
                    } else {
                        // The field with null value has to be passed even the file field is empty.
                        result[field.fieldDefinition.id] = null;
                    }
                } else {
                    result[field.fieldDefinition.id] = field.value;
                }
            } else {
                result[field.fieldDefinition.id] = null;
            }
        });
        return result;
    }

    private _reject(event): void {
        this.buttonOperating = true;
        if (
            this.processDefinition.configuration.status &&
            this.processDefinition.configuration.status.rejected &&
            this.processDefinition.configuration.status.rejected.length > 1
        ) {
            this._selectService
                .open({
                    open: true,
                    isMultiple: false,
                    data: this.processDefinition.configuration.status.rejected,
                    title: null,
                    eventExtraKey: 'slideSheet',
                    eventExtraValue: true,
                })
                .pipe(take(1))
                .subscribe((res) => {
                    if (res.result) {
                        this._sendRejectRequest(
                            this.processDefinition.configuration.status
                                .rejected[res.selectedIndex]
                        );
                    }
                });
        } else {
            this._sendRejectRequest(null);
        }
    }

    private _saveProgressBarExpansionState(isExpanded: boolean) {
        const workflowPreference = new UserWorkflowPreference();
        workflowPreference.isProgressExpanded = isExpanded;
        this._store.dispatch({
            type: UsersActionTypes.UPDATE_USER_SETTING,
            payload: UserWorkflowPreference.toUserSettings(workflowPreference)
        });
    }

    private _serveProgressData(
        projectInstance: ProcessInstance,
        processDef: ProcessDefinition
    ): Array<ProgressNodeModel> {
        const nodes = [];
        // Filter out gateways, only active and done tasks on the list.
        const validTaskInstances = projectInstance.task.filter(
            (item) => item.taskDefinition
        );
        let index = 0;
        const loadStageColorsFunc = (
            taskDef: TaskDefinition,
            node: ProgressNodeModel
        ) => {
            node.foreColor =
                this._workflowService.getTaskStageFontColor(taskDef);
            node.background =
                this._workflowService.getTaskStageBackground(taskDef);
        };
        projectInstance.taskExecuteOrder.forEach((task) => {
            const taskInstance = validTaskInstances.find(
                (ti) => ti.taskDefinition.id === task.id
            );
            let taskDef;
            let node: ProgressNodeModel;
            if (taskInstance) {
                // a task instance
                taskDef = taskInstance.taskDefinition;
                node = ProgressNodeModel.fromTaskInstance(
                    taskInstance,
                    this.task
                );
            } else {
                taskDef = processDef.taskDefinition.find(
                    (tDef) => tDef.id === task.id
                );
                node = ProgressNodeModel.fromTaskDef(taskDef);
            }

            loadStageColorsFunc(taskDef, node);
            node.serialNo = ++index;
            nodes.push(node);
        });

        return nodes;
    }

    /**
     * sets the value change status for a given task.
     * @param id the task id
     * @param isChanged a flag indicating whether the given task is changed.
     */
    private _setTaskValueChangeFlag(id: string, isChanged: boolean) {
        if (!!id) {
            this._valueChangedFlagMap.set(id, isChanged);
            if (this._isCurrentInputTaskDone && this.buttons.length === 1) {
                this.buttons[0].disable = false;
            }
        }
    }

    private _sendBack(): void {
        this.buttonOperating = true;
        this.showListInPopup = false;
        if (!this.task || !this.task.id) {
            // for the workflow only has one approve task, the sendBack request should not be sent.
            return;
        }
        this._taskService
            .sendBackTaskById(
                this.task.id,
                this.backTask.id,
                this.comment ? { comment: this.comment } : null
            )
            .pipe(
                catchError((err) => {
                    if (err.message) {
                        const errmess: string = err.message;
                        if (
                            errmess.indexOf('The task status incorrect!') > -1
                        ) {
                            this._toast.notify({
                                style: NotificationStyles.Error,
                                message:
                                    'Someone else has already finished this task',
                            });
                        } else if (
                            errmess.indexOf('The task owner incorrect!') > -1
                        ) {
                            this._toast.notify({
                                style: NotificationStyles.Error,
                                message:
                                    'Someone else is already working on this task',
                            });
                        } else {
                            this._toast.notify({
                                style: NotificationStyles.Error,
                                message:
                                    'Someone else has already finished this task',
                            });
                        }
                    }
                    return of(null);
                }),
                take(1)
            )
            .subscribe((res) => {
                if (res) {
                    this._toast.notify({
                        style: NotificationStyles.Success,
                        message: `Task '${this.task.name}' successfully sent back.`,
                    });
                }
                this._slideSheetService.slideSheetActionSubject$.next({
                    type: SlideSheetActionTypes.CLOSE,
                    payload: null,
                });
                this._store.dispatch(
                    new ProcessInstanceAddOrUpdate({
                        id: this.instance.id,
                        processDefinitionId: this.processDefinition.id,
                        template: this.processDefinition.template,
                    })
                );
                this.buttonOperating = false;
            });
    }

    private _sendRejectRequest(statusValue: string): void {
        this._taskService
            .approveTaskById(
                this.task.id,
                false,
                statusValue,
                this.comment ? { comment: this.comment } : null
            )
            .pipe(
                catchError((err) => {
                    if (err.message) {
                        this._toast.notify({
                            style: NotificationStyles.Error,
                            message:
                                'Someone else has already finished this task',
                        });
                    }
                    return of(null);
                }),
                take(1)
            )
            .subscribe((res) => {
                if (res) {
                    this._toast.notify({
                        style: NotificationStyles.Success,
                        message: `Task '${this.task.name}' successfully rejected.`,
                    });
                }
                this._slideSheetService.slideSheetActionSubject$.next({
                    type: SlideSheetActionTypes.CLOSE,
                    payload: { isTriggerNewFlagNeedToReset: true },
                });
                this._store.dispatch(
                    new ProcessInstanceAddOrUpdate({
                        id: this.instance.id,
                        processDefinitionId: this.processDefinition.id,
                        template: this.processDefinition.template,
                    })
                );
                this.buttonOperating = false;
            });
    }
    //#endregion2669
}
